/**
 * TrophyTokensLegendModal — iter87.199.3
 *
 * Popup rendered from a volt pill in the Leaderboard footer. Packages
 * the Trophy Tokens brief description, 4-state icon legend, and buy-in
 * / pot math into a dedicated modal so the leaderboard bottom stays
 * clean. Content mirrors the previously-inline footnote 1:1 so nothing
 * is lost — just tucked behind a tap.
 */
import { X, Trophy } from "lucide-react";

export default function TrophyTokensLegendModal({ open, onClose, tournament }) {
    if (!open) return null;
    const preview = tournament?.trophy_tokens_preview;
    if (!preview) return null;

    const buyInCopy = preview.allocation_mode === "per_player"
        ? `${preview.amount} per crewmate`
        : `${preview.amount} total pot`;
    const lockCopy = preview.finalized
        ? "Awards finalized."
        : preview.locked
            ? "Roster locked at first kickoff."
            : "Locks at first kickoff / R1 tee-off.";

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
            data-testid="trophy-tokens-legend-backdrop"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                className="brutal-card max-w-lg w-full max-h-[88dvh] flex flex-col"
                data-testid="trophy-tokens-legend-modal"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between gap-3 p-5 border-b-2 border-[#222]">
                    <div className="flex items-center gap-2 min-w-0">
                        <Trophy className="w-5 h-5 text-volt shrink-0" />
                        <h2 className="font-display font-black text-xl uppercase truncate text-volt">
                            Trophy Tokens
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="brutal-btn-ghost p-1.5"
                        data-testid="trophy-tokens-legend-close-btn"
                        aria-label="Close Trophy Tokens legend"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="overflow-y-auto px-5 py-4 flex-1 text-sm text-zinc-300 leading-relaxed space-y-4">
                    {/* Brief description */}
                    <p data-testid="trophy-tokens-legend-brief" className="text-zinc-300">
                        Trophy Tokens are a Pro+ side-pot that runs on top of your Rivalry
                        Points. Admins opt each crewmate in, everyone confirms before
                        first kickoff, and the pot goes to the tournament winner(s).
                    </p>

                    <hr className="border-t border-white/[0.08]" />

                    {/* Status / legend */}
                    <div data-testid="trophy-tokens-legend-states" className="space-y-2">
                        <h3 className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Status Legend</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                            <div className="flex items-start gap-2">
                                <span className="inline-flex items-center rounded-full bg-volt/15 border border-volt/60 text-volt text-[9px] font-bold px-1.5 py-0.5 shrink-0 mt-0.5">
                                    <Trophy className="w-2.5 h-2.5" />
                                </span>
                                <span className="text-xs"><span className="font-bold text-volt">Blue trophy</span> — Confirmed. In the pot.</span>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="inline-flex items-center rounded-full bg-heat/15 border border-heat/60 text-heat text-[9px] font-bold px-1.5 py-0.5 shrink-0 mt-0.5">
                                    <Trophy className="w-2.5 h-2.5" />
                                </span>
                                <span className="text-xs"><span className="font-bold text-heat">Red trophy</span> — Opted in, waiting on admin confirm.</span>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="inline-flex items-center rounded-full bg-white/[0.03] border border-white/10 text-zinc-500 text-[9px] font-bold px-1.5 py-0.5 shrink-0 mt-0.5 opacity-70">
                                    <Trophy className="w-2.5 h-2.5" />
                                </span>
                                <span className="text-xs"><span className="font-bold text-zinc-400">Empty trophy</span> — Not opted in yet.</span>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="w-[22px] h-[16px] shrink-0 mt-0.5" aria-hidden="true" />
                                <span className="text-xs"><span className="font-bold text-zinc-500">No trophy</span> — Dropped (missed the kickoff window).</span>
                            </div>
                        </div>
                    </div>

                    <hr className="border-t border-white/[0.08]" />

                    {/* Buy-in / pot math */}
                    <div data-testid="trophy-tokens-legend-buyin" className="space-y-2">
                        <h3 className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Buy-in & Pot</h3>
                        <p className="text-xs text-zinc-300 leading-relaxed">
                            Buy-in <span className="text-white font-bold">{buyInCopy}</span>
                            <span className="text-zinc-600">{" · "}</span>
                            <span className="text-white font-bold">{preview.n_opted_in}</span> in the pool
                            {preview.withhold_pct > 0 && (
                                <>
                                    <span className="text-zinc-600">{" · "}</span>
                                    <span className="text-white font-bold">{preview.withhold_pct}%</span> house cut
                                </>
                            )}
                            <span className="text-zinc-600">{" · "}</span>
                            <span className="font-bold text-volt">{preview.winner_pot} tokens</span> up for grabs.
                        </p>
                        <p className="text-[11px] text-zinc-500">{lockCopy}</p>
                    </div>
                </div>

                <div className="p-3 border-t-2 border-[#222] flex justify-end bg-ink">
                    <button
                        type="button"
                        onClick={onClose}
                        className="brutal-btn-primary text-xs py-2"
                        data-testid="trophy-tokens-legend-done-btn"
                    >
                        Got it
                    </button>
                </div>
            </div>
        </div>
    );
}
